import type { Bootstrap, Container, Sip } from '@sbclite/core'

export type PreflightState = 'ready' | 'warning' | 'missing' | 'disabled' | 'error' | 'optional'

export interface PreflightAction {
  readonly label: string
  readonly href: string
}

export interface PreflightItem {
  readonly key: string
  readonly label: string
  readonly summary: string
  readonly state: PreflightState
  readonly required: boolean
  readonly action: PreflightAction
  readonly details: readonly string[]
}

export interface PreflightSection {
  readonly title: string
  readonly description: string
  readonly items: readonly PreflightItem[]
}

export interface PreflightModel {
  readonly title: string
  readonly description: string
  readonly sections: readonly PreflightSection[]
  readonly overallState: Exclude<PreflightState, 'optional'>
}

function overallStateForItems(items: readonly PreflightItem[]): Exclude<PreflightState, 'optional'> {
  if (items.some((item) => item.required && item.state === 'error')) {
    return 'error'
  }

  if (items.some((item) => item.required && item.state === 'missing')) {
    return 'missing'
  }

  if (items.some((item) => item.required && item.state === 'disabled')) {
    return 'disabled'
  }

  if (items.some((item) => item.required && item.state === 'warning')) {
    return 'warning'
  }

  return 'ready'
}

function sectionState(section: PreflightSection): Exclude<PreflightState, 'optional'> {
  return overallStateForItems(section.items)
}

function modelState(sections: readonly PreflightSection[]): Exclude<PreflightState, 'optional'> {
  const items = sections.flatMap((section) => section.items)
  return overallStateForItems(items)
}

function itemDetails(value: string | null | undefined, fallback: string): readonly string[] {
  return value ? [value] : [fallback]
}

export function buildBootstrapPreflightModel(status: Bootstrap.SipEngineBootstrapStatus): PreflightModel {
  const sections: readonly PreflightSection[] = [
    {
      title: 'Required bootstrap inputs',
      description: 'The first-call setup path needs these database-backed objects before the wizard can complete.',
      items: [
        {
          key: 'containerProfiles',
          label: 'Container profiles',
          summary: status.containerProfiles > 0 ? `${status.containerProfiles} profile(s) available.` : 'No container profile exists yet.',
          state: status.containerProfiles > 0 ? 'ready' : 'missing',
          required: true,
          action: { label: 'Open container profiles', href: '/system/container-profiles' },
          details: itemDetails(status.containerProfiles > 0 ? null : 'Create a container profile before bootstrapping a SIP engine.', 'Container profiles are ready.'),
        },
        {
          key: 'mediaRealms',
          label: 'Media realms',
          summary: status.mediaRealms > 0 ? `${status.mediaRealms} realm(s) available.` : 'No media realm exists yet.',
          state: status.mediaRealms > 0 ? 'ready' : 'missing',
          required: true,
          action: { label: 'Open media realms', href: '/foundation/media-realms' },
          details: itemDetails(status.mediaRealms > 0 ? null : 'Create a media realm with RTP address and port range.', 'Media realms are ready.'),
        },
        {
          key: 'sipInterfaces',
          label: 'SIP interfaces',
          summary: status.sipInterfaces > 0 ? `${status.sipInterfaces} interface(s) available.` : 'No SIP interface exists yet.',
          state: status.sipInterfaces > 0 ? 'ready' : 'missing',
          required: true,
          action: { label: 'Open SIP interfaces', href: '/foundation/sip-interfaces' },
          details: itemDetails(status.sipInterfaces > 0 ? null : 'Create a SIP interface before bootstrapping the engine.', 'SIP interfaces are ready.'),
        },
        {
          key: 'profileSipInterfaces',
          label: 'Profile ↔ interface links',
          summary: status.profileSipInterfaces > 0 ? `${status.profileSipInterfaces} link(s) available.` : 'No profile-to-interface link exists yet.',
          state: status.profileSipInterfaces > 0 ? 'ready' : 'missing',
          required: true,
          action: { label: 'Open container profiles', href: '/system/container-profiles' },
          details: itemDetails(status.profileSipInterfaces > 0 ? null : 'Attach at least one SIP interface to a container profile.', 'Profile-to-interface links are ready.'),
        },
      ],
    },
    {
      title: 'Container assignment',
      description: 'The active engine needs a live container identity and a resolved profile assignment.',
      items: [
        {
          key: 'containerInstances',
          label: 'Container instances',
          summary: status.containerInstances > 0 ? `${status.containerInstances} container(s) registered.` : 'No container instance is registered yet.',
          state: status.containerInstances > 0 ? 'ready' : 'missing',
          required: true,
          action: { label: 'Open containers', href: '/system/containers' },
          details: itemDetails(status.containerInstances > 0 ? null : 'Register the runtime container before initialization.', 'Container instances are ready.'),
        },
        {
          key: 'profileAssignments',
          label: 'Profile assignments',
          summary: status.profileAssignments > 0 ? `${status.profileAssignments} assignment(s) available.` : 'No profile assignment exists yet.',
          state: status.profileAssignments > 0 ? 'ready' : 'missing',
          required: true,
          action: { label: 'Open containers', href: '/system/containers' },
          details: itemDetails(status.profileAssignments > 0 ? null : 'Assign a container profile to the target container.', 'Profile assignments are ready.'),
        },
        {
          key: 'unassignedContainers',
          label: 'Unassigned containers',
          summary: status.unassignedContainers === 0 ? 'All registered containers are assigned.' : `${status.unassignedContainers} container(s) still need assignment.`,
          state: status.unassignedContainers === 0 ? 'optional' : 'warning',
          required: false,
          action: { label: 'Review containers', href: '/system/containers' },
          details: itemDetails(status.unassignedContainers === 0 ? null : 'Review leftover containers after bootstrap completes.', 'No unassigned containers remain.'),
        },
      ],
    },
  ]

  return {
    title: 'Bootstrap preflight',
    description: 'Current database state for the first-run SIP engine setup path.',
    sections,
    overallState: modelState(sections),
  }
}

export function buildContainerPreflightModel(report: Container.ContainerPrerequisiteReport): PreflightModel {
  const sections: readonly PreflightSection[] = [
    {
      title: 'Engine readiness',
      description: 'Actual backend prerequisite checks for the active container.',
      items: report.checks.map((check) => ({
        key: check.key,
        label: check.label,
        summary: check.summary,
        state: check.state,
        required: check.blocking,
        action: {
          label: check.key === 'profileAssignment' ? 'Open container profiles' : check.key === 'hostBinding' || check.key === 'sipInterface' ? 'Open SIP interfaces' : 'Open media realms',
          href: check.key === 'profileAssignment'
            ? '/system/container-profiles'
            : check.key === 'mediaRealm'
              ? '/foundation/media-realms'
              : '/foundation/sip-interfaces',
        },
        details: check.details,
      })),
    },
    {
      title: 'Interface completeness',
      description: 'Every enabled SIP interface is evaluated by the backend, including wildcard-bind edge cases.',
      items: report.sipInterfaces.length > 0
        ? report.sipInterfaces.map((sipInterface) => ({
            key: sipInterface.id,
            label: sipInterface.interfaceId,
            summary: sipInterface.complete ? 'Interface is complete.' : 'Interface has unresolved prerequisites.',
            state: sipInterface.complete ? 'ready' : 'missing',
            required: true,
            action: { label: 'Open SIP interfaces', href: '/foundation/sip-interfaces' },
            details: sipInterface.issues,
          }))
        : [{
            key: 'no-interfaces',
            label: 'SIP interfaces',
            summary: 'No enabled SIP interfaces are linked to the active container profile.',
            state: 'missing',
            required: true,
            action: { label: 'Open SIP interfaces', href: '/foundation/sip-interfaces' },
            details: ['Create and link a SIP interface to the active container profile.'],
          }],
    },
  ]

  return {
    title: 'Container preflight',
    description: 'Resolved startup and routing prerequisites for the selected container.',
    sections,
    overallState: modelState(sections),
  }
}

export function buildSipPeerPreflightModel(view: Sip.SipPeerEffectivePolicyView): PreflightModel {
  const peer = view.peer
  const policy = view.policy
  const missingSections = policy.available ? [] : policy.report.missingSections

  const mapAttachedState = (state: Sip.SipPeerPolicyReferenceState, required: boolean): PreflightState => {
    if (state === 'ready' || state === 'optional') return state;
    if (state === 'disabled') return 'disabled';
    if (state === 'error') return 'error';
    return required ? 'missing' : 'optional';
  }

  const placementState = (candidate: Container.EnginePlacementCandidate | null): PreflightState => {
    if (!candidate) return 'missing';
    if (candidate.runtime.runtimeReadyState === 'ready' && candidate.peerFitReady !== false && candidate.configurationReady) return 'ready';
    if (candidate.runtime.runtimeReadyState === 'not-ready') return 'error';
    return 'warning';
  }

  const placementSummary = (candidate: Container.EnginePlacementCandidate): string => {
    return `${candidate.container.containerId}: configuration ${candidate.configurationReady ? 'ready' : 'not ready'}, runtime ${candidate.runtime.runtimeReadyState}.`;
  }

  const sections: readonly PreflightSection[] = [
    {
      title: 'Resolved peer prerequisites',
      description: 'These items come from the peer record and the resolved effective-policy payload.',
      items: [
        {
          key: 'sipProfile',
          label: 'SIP profile',
          summary: policy.profile
            ? policy.available
              ? `Using ${policy.profile.name} (${policy.profile.source}).`
              : `Profile ${policy.profile.name} is attached, but required sections are still missing.`
            : 'No SIP profile is attached or resolved.',
          state: policy.profile ? (policy.available ? 'ready' : 'missing') : 'missing',
          required: true,
          action: { label: 'Open SIP profiles', href: '/connectivity/sip-profiles' },
          details: policy.profile
            ? policy.available
              ? [`Resolved from ${policy.profile.source}.`]
              : [`Resolved from ${policy.profile.source}.`, `Missing sections: ${policy.report.missingSections.join(', ')}.`]
            : ['Attach a SIP profile to centralize policy.'],
        },
        {
          key: 'peerHost',
          label: 'Peer host',
          summary: view.peerHost.summary,
          state: mapAttachedState(view.peerHost.state, false),
          required: false,
          action: { label: 'Open hosts', href: '/foundation/hosts' },
          details: view.peerHost.details,
        },
        {
          key: 'sipInterface',
          label: 'SIP interface',
          summary: view.sipInterface.summary,
          state: mapAttachedState(view.sipInterface.state, true),
          required: true,
          action: { label: 'Open SIP interfaces', href: '/foundation/sip-interfaces' },
          details: view.sipInterface.details,
        },
        {
          key: 'interfaceHost',
          label: 'Interface host',
          summary: view.interfaceHost.summary,
          state: mapAttachedState(view.interfaceHost.state, false),
          required: false,
          action: { label: 'Open hosts', href: '/foundation/hosts' },
          details: view.interfaceHost.details,
        },
        {
          key: 'mediaRealm',
          label: 'Media realm',
          summary: view.mediaRealm.summary,
          state: mapAttachedState(view.mediaRealm.state, true),
          required: true,
          action: { label: 'Open media realms', href: '/foundation/media-realms' },
          details: view.mediaRealm.details,
        },
        {
          key: 'tlsContext',
          label: 'TLS context',
          summary: peer?.transport === 'TLS'
            ? view.peerTlsContext.summary
            : view.peerTlsContext.summary,
          state: peer?.transport === 'TLS'
            ? mapAttachedState(view.peerTlsContext.state, true)
            : 'optional',
          required: peer?.transport === 'TLS',
          action: { label: 'Open TLS contexts', href: '/foundation/tls-contexts' },
          details: peer?.transport === 'TLS'
            ? ['TLS transport requires an explicit TLS context for secure signaling.']
            : ['This peer does not require TLS signaling.'],
        },
        {
          key: 'interfaceTlsContext',
          label: 'Interface TLS context',
          summary: view.interfaceTlsContext.summary,
          state: inputState(view.interfaceTlsContext.state),
          required: view.sipInterface.value?.protocol === 'TLS',
          action: { label: 'Open TLS contexts', href: '/foundation/tls-contexts' },
          details: view.interfaceTlsContext.details,
        },
      ],
    },
    {
      title: 'Engine placement',
      description: 'Concrete SipEngine candidates, their DB assignment state, and observed runtime freshness.',
      items: [
        {
          key: 'placementSelection',
          label: 'Placement selection',
          summary: view.placement.selectionRequired
            ? 'Select a container to inspect a concrete placement target.'
            : view.placement.summary,
          state: view.placement.selectionRequired
            ? 'missing'
            : view.placement.selectedContainer?.configurationReady && view.placement.selectedContainer.runtime.runtimeReadyState === 'ready'
              ? 'ready'
              : 'warning',
          required: true,
          action: { label: 'Open containers', href: '/system/containers' },
          details: view.placement.selectionRequired
            ? ['No containerId context was supplied; selection is required.']
            : [view.placement.selectedContainerId ? `Selected container: ${view.placement.selectedContainerId}.` : 'No container selected.'],
        },
        {
          key: 'placementRuntime',
          label: 'Selected runtime',
          summary: view.placement.selectedContainer
            ? `${view.placement.selectedContainer.container.containerId}: runtime ${view.placement.selectedContainer.runtime.runtimeReadyState}.`
            : 'No selected container runtime is available.',
          state: placementState(view.placement.selectedContainer),
          required: true,
          action: { label: 'Open containers', href: '/system/containers' },
          details: view.placement.selectedContainer
            ? [
                `Observed registry status: ${view.placement.selectedContainer.runtime.observedStatus}.`,
                `Observed heartbeat: ${view.placement.selectedContainer.runtime.observedAt}.`,
                view.placement.selectedContainer.reasons.length > 0 ? view.placement.selectedContainer.reasons.join(' ') : 'No blocking reasons.',
              ]
            : ['Choose a candidate to inspect runtime evidence.'],
        },
        {
          key: 'placementCandidates',
          label: 'Candidate list',
          summary: view.placement.candidates.length > 0
            ? `${view.placement.candidates.length} candidate SipEngine container(s) are available.`
            : 'No SipEngine candidates are available.',
          state: view.placement.candidates.length > 0 ? 'optional' : 'missing',
          required: false,
          action: { label: 'Open containers', href: '/system/containers' },
          details: view.placement.candidates.length > 0
            ? view.placement.candidates.map((candidate) => placementSummary(candidate))
            : ['No SipEngine container rows were returned by the backend.'],
        },
      ],
    },
    {
      title: 'Effective policy resolution',
      description: 'The resolved SIP profile sections shown here come directly from the backend policy API.',
      items: [
        {
          key: 'sessionTimers',
          label: 'Session timers',
          summary: policy.sessionTimers ? 'Resolved session timer policy is available.' : 'No session timer policy was resolved.',
          state: policy.sessionTimers ? 'ready' : missingSections.includes('sessionTimers') ? 'missing' : 'warning',
          required: true,
          action: { label: 'Open SIP profiles', href: '/connectivity/sip-profiles' },
          details: policy.sessionTimers ? [policy.sessionTimers.provenance] : ['Resolve the attached profile to populate session timer settings.'],
        },
        {
          key: 'registrar',
          label: 'Registrar policy',
          summary: policy.registrar ? 'Resolved registrar policy is available.' : 'No registrar policy was resolved.',
          state: policy.registrar ? 'ready' : missingSections.includes('registrar') ? 'missing' : 'warning',
          required: true,
          action: { label: 'Open SIP profiles', href: '/connectivity/sip-profiles' },
          details: policy.registrar ? [policy.registrar.provenance] : ['Resolve the attached profile to populate registrar settings.'],
        },
        {
          key: 'digest',
          label: 'Digest policy',
          summary: policy.digest ? 'Resolved digest policy is available.' : 'No digest policy was resolved.',
          state: policy.digest ? 'ready' : missingSections.includes('digest') ? 'missing' : 'warning',
          required: true,
          action: { label: 'Open SIP profiles', href: '/connectivity/sip-profiles' },
          details: policy.digest ? [policy.digest.provenance] : ['Resolve the attached profile to populate digest settings.'],
        },
        {
          key: 'media',
          label: 'Media policy',
          summary: policy.media ? 'Resolved media policy is available.' : 'No media policy was resolved.',
          state: policy.media ? 'ready' : missingSections.includes('media') ? 'missing' : 'warning',
          required: true,
          action: { label: 'Open SIP profiles', href: '/connectivity/sip-profiles' },
          details: policy.media ? [policy.media.provenance] : ['Resolve the attached profile to populate media settings.'],
        },
        {
          key: 'cryptoSuites',
          label: 'Crypto suites',
          summary: policy.cryptoSuites ? 'Resolved crypto-suite order is available.' : 'No crypto-suite policy was resolved.',
          state: policy.cryptoSuites ? 'ready' : missingSections.includes('cryptoSuites') ? 'missing' : 'warning',
          required: true,
          action: { label: 'Open SIP profiles', href: '/connectivity/sip-profiles' },
          details: policy.cryptoSuites ? [policy.cryptoSuites.provenance] : ['Resolve the attached profile to populate crypto-suite settings.'],
        },
      ],
    },
    {
      title: 'Manipulation chain',
      description: 'Manipulation remains an optional operator override, but the checklist shows the actual runtime order.',
      items: [
        {
          key: 'interfaceManipulation',
          label: 'Interface pre-classification',
          summary: view.interfaceManipulation.summary,
          state: inputState(view.interfaceManipulation.state),
          required: false,
          action: { label: 'Open SIP manipulations', href: '/connectivity/sip-manipulations' },
          details: view.interfaceManipulation.details,
        },
        {
          key: 'peerInboundManipulation',
          label: 'Peer inbound',
          summary: view.peerInboundManipulation.summary,
          state: inputState(view.peerInboundManipulation.state),
          required: false,
          action: { label: 'Open SIP manipulations', href: '/connectivity/sip-manipulations' },
          details: view.peerInboundManipulation.details,
        },
        {
          key: 'peerOutboundManipulation',
          label: 'Peer outbound',
          summary: view.peerOutboundManipulation.summary,
          state: inputState(view.peerOutboundManipulation.state),
          required: false,
          action: { label: 'Open SIP manipulations', href: '/connectivity/sip-manipulations' },
          details: view.peerOutboundManipulation.details,
        },
      ],
    },
    {
      title: 'Classification and routing source',
      description: 'Routing settings and ordered rule inventory are backend-resolved for this peer.',
      items: [
        {
          key: 'routingSettings',
          label: 'Classification mode',
          summary: view.routing.summary,
          state: view.routing.settings ? 'ready' : 'missing',
          required: true,
          action: { label: 'Open routing rules', href: '/intelligence/routing-rules' },
          details: view.routing.settings ? [`Routing settings row ${view.routing.settings.id}.`] : ['Create the routing settings row to enable classification.'],
        },
        {
          key: 'routingRules',
          label: 'Routing rules',
          summary: view.routing.enabledRules.length > 0
            ? `${view.routing.enabledRules.length} enabled rule(s) across ${view.routing.allRules.length} total.`
            : 'No enabled routing rules are available.',
          state: view.routing.enabledRules.length > 0 ? 'ready' : 'missing',
          required: true,
          action: { label: 'Open routing rules', href: '/intelligence/routing-rules' },
          details: view.routing.allRules.length > 0
            ? [`Ordered by priority. Disabled rules remain visible in the backend snapshot.`]
            : ['No routing rules were returned by the backend.'],
        },
      ],
    },
  ]

  return {
    title: 'Peer preflight',
    description: 'Resolved prerequisites and effective policy for the active peer.',
    sections,
    overallState: modelState(sections),
  }
}

function inputState(state: Sip.SipPeerPolicyReferenceState): PreflightState {
  switch (state) {
    case 'ready':
      return 'ready'
    case 'disabled':
      return 'disabled'
    case 'error':
      return 'error'
    case 'optional':
      return 'optional'
    case 'missing':
      return 'missing'
  }
}

export function sectionOverallState(section: PreflightSection): Exclude<PreflightState, 'optional'> {
  return sectionState(section)
}
